import * as Sentry from '@sentry/react'
import WallspiceWebgl from 'Renderer/WallspiceWebgl'
import RenderItem from 'Renderer/RenderItem'

import * as THREE from 'three'
import * as WallSnap from 'Renderer/WallSnap'
import isMobile from 'utils/isMobile'

import { DEFAULT_THREE_NATIVE_LAYER } from 'Renderer/constants'

class FurnitureItem extends RenderItem {
    constructor(id, renderer, position, onUpdate, furnitureData, updateReact) {
        super(id, renderer, position, true, onUpdate, updateReact)

        this.furnitureData = furnitureData

        this.parent = null
        this.planeId = null

        this.selected = false
        this.corners = null
        this.rotation = null
        this.orientation = furnitureData.heading
        this.tagPointLocal = new THREE.Vector3()

        this.layer = 1

        // Static stackable surfaces are assigned a separate rendering layer to isolate shadows
        this.ThreeNtvLayer = this.furnitureData.stacks && this.furnitureData.stackedPlane 
            ? (this.Renderer.stackingPlanes.get(this.furnitureData.stackedPlane) || {}).ThreeNtvLayer
            : DEFAULT_THREE_NATIVE_LAYER

        this.tmpMat4 = new THREE.Matrix4()
        this.scalemat4 = new THREE.Matrix4()
        this.rotmat4 = new THREE.Matrix4()
        this.orientationmat4 = new THREE.Matrix4()

        this.marker = null
        this.boundingBox = null
        this.loadFurniture()
        this.planeIndex = null
        this.lastUpdateFraction = null

        if (this.furnitureData && this.furnitureData.stacks && this.furnitureData.stackedPlane) {
            this.initialSetPlane(this.furnitureData.stackedPlane, position)
        }
        this.position = position
        this.setPosition(position)

        // to handle visual and mouseover states
        this.isSelected = false
        this.isUnderMouse = false
    }

    getCorners() {
        return this.corners
    }

    getRotation() {
        return this.rotation
    }

    getOrientation() {
        return this.orientation
    }

    setOrientation(orientation) {
        const { wallCollisions } = this.Renderer.settings

        if( wallCollisions && ! this.isStackable() )
        {
            WallSnap.setOrientation(this, orientation)
        }
        else
        {
            this.orientation = orientation
        }

        this.update()
    }

    updateFurniture(furnitureData) {
        this.furnitureData = furnitureData
        this.loadFurniture()
    }

    initialSetPlane(planeId, position2d) {
        const surface = this.Renderer.getStackingSurface(planeId)
        if (!surface) {
            this.position = position2d // save for delayed initialization
            this.Renderer.addDelayedStackedLink(planeId, this)
            return
        }
        this.setPlane(planeId)
    }

    delayedSetPlane(planeId) {
        this.setPlane(planeId)
        this.setPosition(this.position)
    }

    setPlane(planeId, xy = null, modifyOutlineState = false) {
        // A missing planeId means that the stackable item is being
        // placed on the floor. In cases where the stackable
        // previously sat on furniture the caller is expected to
        // provide this function advanced notice of the object's new
        // `xy` position on the floor. This is so that physics
        // metadata for wall snapping can be intialized correctly. We
        // can't use a stale `xy` position and then have the caller
        // update us with the new `xy` position later. This could
        // produce an abrupt warp across the room which could cause
        // surprising wall collisions. Once the furniture item is
        // under the control of the physics system motion should
        // happen in small increments. The following block makes sure
        // that initPhysicsEntity() sees the object's new floor
        // position right away.
        //
        // TODO: It would be preferable to have initPhysicsEntity()
        // take a suite of parameters instead of reading data (such
        // as position) off of the RenderItem object directly. This
        // will be part of a coming wall snapping refactor. 

        const noPlane = (planeId === null || planeId === undefined)

        if (noPlane && xy) {
            this.position = xy
            this.initPhysicsEntity()
        }

        this.stackedPlane = planeId
        
        const surface = this.Renderer.getStackingSurface(this.stackedPlane)
        this.ThreeNtvLayer = surface ? surface.ThreeNtvLayer : DEFAULT_THREE_NATIVE_LAYER 
        this.renderObject.traverse(obj => obj.layers.set(this.ThreeNtvLayer))

        this.refreshParentSurface(modifyOutlineState)
    }

    addParentToHighlight() {
        const rootObject = this.findRootParent()
        if (!rootObject) {
            return
        }
        rootObject.isSelected = true
        if (rootObject.renderObject) {
            rootObject.renderObject.isSelected = rootObject.isSelected
        }
        rootObject.enableOutline()
        rootObject.updateOutlineThickness()
    }

    removeParentFromHighlight() {
        const rootObject = this.findRootParent()
        if (!rootObject) {
            return
        }
        rootObject.isSelected = false
        if (rootObject.renderObject) {
            rootObject.renderObject.isSelected = rootObject.isSelected
        }
        if (!rootObject.isUnderMouse) {
            rootObject.disableOutline()
        }
        rootObject.updateOutlineThickness()
    }

    refreshParentSurface(modifyOutlineState = false) {
        if (this.parent) {
            if (modifyOutlineState)
                this.removeParentFromHighlight()
            this.parent.removeChild(this)
            this.parent = null
        } else {
            this.Renderer.removeFromScene(this)
        }

        const surface = this.Renderer.getStackingSurface(this.stackedPlane)
        if (surface && surface.parent) {
            const parentItem = this.Renderer.getRenderItem(surface.parent.uniqueId)
            if (parentItem) {
                this.parent = parentItem
                parentItem.addChild(this)
                if (modifyOutlineState)
                    this.addParentToHighlight()
            }
        } else {
            this.Renderer.addToScene(this)

            if (modifyOutlineState)
                this.addParentToHighlight()
        }
    }

    refreshCombinableParent() {
        if (!this.isCombinableChild()) {
            return
        }
        const parentItem = this.Renderer.getRenderItem(this.furnitureData.combinablePlacement.parentId)
        if (!parentItem) {
            return
        }

        // TODO: optimize to minimeze these "recoursive" calls
        if (parentItem.refreshCombinableParent) {
            parentItem.refreshCombinableParent()
        }

        if (this.parent) {
            this.parent.removeChild(this)
            this.parent = null
        } else {
            this.Renderer.removeFromScene(this)
        }
        this.parent = parentItem
        parentItem.addChild(this)
        this.Renderer.addToRenderItems(this)
        const matrix = new THREE.Matrix4()
        matrix.set(...this.furnitureData.combinablePlacement.localTransform)
        this.renderObject.matrix.identity()
        this.renderObject.applyMatrix4(matrix)
        this.renderObject.updateMatrixWorld()

        const rootParent = this.findRootParent()
        if (rootParent && rootParent.renderObject) {
            const prevMatrix = new THREE.Matrix4().copy(rootParent.renderObject.matrix)
            rootParent.renderObject.matrix.identity()
            rootParent.bbox.setFromObject(rootParent.renderObject)

            rootParent.renderObject.matrix.copy(prevMatrix)
            rootParent.renderObject.updateMatrixWorld()
            rootParent.update()
        }
    }

    addJustLoaded() {
        super.addJustLoaded()
        this.refreshParentSurface()
        this.refreshCombinableParent()
        if (this.isSelected) {
            this.onSelect()
        }
    }

    isStackable() {
        return (
            this.stackedPlane !== undefined
            && this.stackedPlane !== null
        )
    }

    isCombinableChild() {
        return this.furnitureData
            && this.furnitureData.combinablePlacement
            && this.furnitureData.combinablePlacement.parentId
    }

    setPosition(xy, options = {}) {
        const { wallCollisions } = this.Renderer.settings
        if (this.isStackable()) {
            this.setStackedPosition(xy, this.stackedPlane)
        } else if (wallCollisions) {
            WallSnap.setPosition(this, xy)
        } else if (!this.isCombinableChild()) {
            super.setPosition(xy, options)
        }

        this.update()
        return true
    }

    setStackedPosition(xy, stackedSurfaceId) {
        if (!Array.isArray(xy)) {
            return false
        }

        const surface = this.Renderer.getStackingSurface(stackedSurfaceId)
        if (!surface || !surface.renderObject) {
            return false
        }

        const renderDimensions = this.Renderer.getRenderDimensions()
        if (!renderDimensions) {
            return false
        }
        this.Renderer.setupRaycaster(xy[0] / renderDimensions[0], xy[1] / renderDimensions[1])
        const intersections = this.Renderer.raycaster.intersectObject(surface.renderObject)

        // TODO: temporary, calculate correct rotation
        const rot = surface.frame()

        if (intersections && intersections.length > 0) {
            let p = intersections[0].point
            if (this.parent) {
                p = this.parent.getObject().worldToLocal(p)
            }
            this.position = xy
            this.pose = {
                pos: [p.x, p.y, p.z],
                rot,
                scale: 1.0
                // depth,
            }
            return true
        }
        return false
    }

    setVisible(shouldBeVisible) {
        super.setVisible(shouldBeVisible)
        this.update()
    }

    setSelected(shouldBeSelected) {
        this.selected = shouldBeSelected
        this.update()
    }

    update(oneTimeRenderCallback = null) {
        if (!this.getObject()) {
            return
        }
        const { Renderer } = this

        if (this.isCombinableChild()) {
            this.getObject().updateMatrixWorld(true)
            const corners = WallspiceWebgl.projBoundBox(this.getObject().matrixWorld, this.bbox,
                this.Renderer.camera.projectionMatrix)
            this.onUpdate({
                corners,
                position: this.getPosition(),
            })
            Renderer.onChildUpdate(oneTimeRenderCallback)
            return
        }

        const pose = this.getPose()
        if (!pose) {
            Renderer.onChildUpdate(oneTimeRenderCallback)
            return
        }

        const rotation = pose.rot
        const position = pose.pos

        WallspiceWebgl.createMat4(rotation, position, this.tmpMat4)

        // this.orientationmat4.makeRotationZ(this.parent ? -this.orientation : this.orientation)
        this.orientationmat4.makeRotationZ(this.orientation)

        this.scalemat4.makeScale(this.furnitureData.scale, this.furnitureData.scale, this.furnitureData.scale)
        this.rotmat4.makeRotationX(this.furnitureData.rotX)

        this.tmpMat4.multiply(this.scalemat4)
        this.tmpMat4.multiply(this.orientationmat4)
        this.tmpMat4.multiply(this.rotmat4)

        this.getObject().matrix.copy(this.tmpMat4)
        this.getObject().updateMatrixWorld(true)

        const corners = WallspiceWebgl.projBoundBox(this.getObject().matrixWorld, this.bbox,
            this.Renderer.camera.projectionMatrix)

        const tagPointWorldCoords = this.renderObject.localToWorld(this.tagPointLocal.clone())
        const tagPoint = tagPointWorldCoords.project(this.Renderer.camera)
        tagPoint.x = Math.min(Math.max(tagPoint.x * 0.5 + 0.5, 0.025), 0.975) * 100
        tagPoint.y = Math.min(Math.max(1.0 - (tagPoint.y * 0.5 + 0.5), 0.05), 0.95) * 100

        this.rotation = pose.rot
        this.corners = corners

        if (this.planeIndex !== pose.planeIndex) {
            this.planeIndex = pose.planeIndex
        }

        Renderer.onChildUpdate(oneTimeRenderCallback)
        this.onUpdate({
            corners,
            tagPoint,
            position: this.getPosition(),
        })

        this.children.forEach((child) => {
            if (child.update) {
                child.update()
            }
        })
    }

    position3Dto2D() {
        const position = new THREE.Vector3()
        this.renderObject.getWorldPosition(position)

        const screenPos = position.project(this.Renderer.camera)
        screenPos.x = screenPos.x * 0.5 + 0.5
        screenPos.y = screenPos.y * 0.5 + 0.5
        screenPos.y = 1.0 - screenPos.y

        const renderDimensions = this.Renderer.getRenderDimensions()

        return {
            x: screenPos.x * renderDimensions[0],
            y: screenPos.y * renderDimensions[1]
        }
    }

    updateReactSide() {
        const { x, y } = this.position3Dto2D()
        this.position = [x, y]
        this.updateReact(x, y)
    }

    initPhysicsEntity() {
        const { wallCollisions } = this.Renderer.settings
        if (!wallCollisions) {
            return
        }
        const fd = this.furnitureData

        let sx = 0.5 / fd.scale
        let sz = 0.5 / fd.scale

        if (fd.min && fd.max) {
            sx = fd.max.x - fd.min.x
            sz = fd.max.z - fd.min.z
        }

        this.body = WallSnap.initBody(this, sx, sz)
        WallSnap.pushOut(this)
    }

    addPlaceholder() {
        this.placeholderGroup = new THREE.Group();

        let sx = 0.5 / this.furnitureData.scale
        let sy = 0.5 / this.furnitureData.scale
        let sz = 0.5 / this.furnitureData.scale
        if (this.furnitureData.min && this.furnitureData.max) {
            sx = this.furnitureData.max.x - this.furnitureData.min.x
            sy = this.furnitureData.max.y - this.furnitureData.min.y
            sz = this.furnitureData.max.z - this.furnitureData.min.z
        }
        const geometry = new THREE.BoxGeometry(sx, sy, sz)
        if (this.furnitureData.min) {
            geometry.translate(
                this.furnitureData.min.x + sx * 0.5,
                this.furnitureData.min.y + sy * 0.5,
                this.furnitureData.min.z + sz * 0.5,
            )
        } else {
            geometry.translate(0, 0.5 * sy, 0)
        }
        this.placeholder = new THREE.Mesh(geometry, new THREE.MeshPhongMaterial({
            color: 0xFFFFFF,
            emissive: 0x888888,
            opacity: 0.5,
            transparent: true,
        }))

        this.placeholder.matrixAutoUpdate = false
        this.placeholder.visible = this.visible
        this.placeholder.castShadow = false
        this.placeholder.receiveShadow = true

        this.bbox.makeEmpty()
        this.bbox.expandByObject(this.placeholder)

        this.placeholderGroup.name = this.name
        this.placeholderGroup.add(this.placeholder)
        this.placeholderGroup.matrixAutoUpdate = false
        this.placeholderGroup.visible = this.visible

        const marker = new THREE.Mesh(geometry, new THREE.MeshPhongMaterial({
            color: 0xFFFFFF,
            emissive: 0x888888,
        }))
        marker.name = 'marker'
        marker.castShadow = true
        marker.receiveShadow = true
        this.placeholderGroup.add(marker)

        this.placeholderGroup.traverse(obj => obj.layers.set(this.ThreeNtvLayer))

        this.marker = marker
        this.lastUpdateFraction = 0.0
        this.setObject(this.placeholderGroup)
        this.addToScene()
        this.updatePlaceholder(0.0)

        this.initPhysicsEntity()

        this.updateReact(...this.position)
    }

    updatePlaceholder(fraction) {
        const quantFraction = Math.floor(fraction * 10.0) / 10.0
        const limitedFraction = Math.max(0.001, Math.min(quantFraction, 0.9))

        if (limitedFraction !== this.lastUpdateFraction) {
            if (this.marker) {
                if (this.furnitureData.rotX === 0) {
                    this.marker.scale.z = limitedFraction
                } else {
                    this.marker.scale.y = limitedFraction
                }
                if(this.Renderer.settings.showItemLoaders){
                    this.update()
                }
            }
            this.lastUpdateFraction = limitedFraction
        }
    }

    clearObject() {
        if (this.marker) {
            this.marker = null
        }
        if (this.parent) {
            this.parent.removeChild(this)
        }
        if (this.getObject()) {
            this.removeFromScene()
            this.unsetObject()
        }
        this.placeholderGroup = null
    }

    findRootParent() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let rootParent = this
        while(rootParent.parent) {
            rootParent = rootParent.parent
        }
        // found rootParent still supposed to have a parent itself, but maybe it's not ready yet
        if (rootParent.isCombinableChild()) {
            return null
        }
        return rootParent
    }

    onHover() {
        this.isUnderMouse = true

        const rootObject = this.findRootParent()
        if (!rootObject) {
            return
        }
        rootObject.enableOutline()
        rootObject.updateOutlineThickness()
    }

    onHoverEnd() {
        this.isUnderMouse = false
        const rootObject = this.findRootParent()
        if (!rootObject) {
            return
        }
        if (!rootObject.isSelected) {
            rootObject.disableOutline()
        }
        rootObject.updateOutlineThickness()
    }

    onSelect() {
        this.addParentToHighlight()
    }

    onSelectEnd() {
        this.removeParentFromHighlight()
    }

    updateOutlineThickness() {
        if (!this.Renderer) {
            return
        }
        // assuming here that hover+selcted can happen only when a single object is outlined,
        // muliple objects can't be selcted and hovered over at the same time
        this.Renderer.setThickOutline(this.isUnderMouse && this.isSelected)
    }

    initializeTagPointPosition() {
        // Initializes the white tag dot position in 3D local coordinates
        // Define utility values that are used along the point selection logic
        const leastItemHeight = 0.3
        const leastNonTableHeight = 0.6
        const furnitureItemHeight = this.bbox.max.y - this.bbox.min.y
        const maxIntersections = 10
        const maxSearchTrials = 5
        const intersectionShiftStep = 0.025
        const maxPointDistanceToCenter = furnitureItemHeight * 0.35

        // For very tall/short furniture items, the bbox center is used as the tag point
        if (this.isStackable() || furnitureItemHeight < leastItemHeight) {
            this.tagPointLocal = this.bbox.getCenter(new THREE.Vector3())
            return    
        }

        // Calculate downward ray for intersection using the bbox dimensions
        const centerPointLocal = this.bbox.getCenter(new THREE.Vector3())
        const topPointLocal = centerPointLocal.clone().setY(this.bbox.max.y)
        const topPoint = this.renderObject.localToWorld(topPointLocal)
        
        // Note THREE.raycaster doesn't support intersecting object groups so they're split into child meshes
        const furnitureMeshList = this.renderObject.children
            .reduce((list, child) => [
                ...list, 
                ...child instanceof THREE.Group ? child.children : [],
                ...child instanceof THREE.Mesh ? [child, ...child.children] : [],
            ], [])

        // Calculate intersection points of the downward ray with the furniture geometry
        // Note some furniture items have central holes so a proper search for the intersection surface is needed
        let trials = 0
        let intersections = []
        const searchCenter = topPoint.clone()
        const raycaster = new THREE.Raycaster(searchCenter, new THREE.Vector3(0, -1, 0))
        while (intersections.length < maxIntersections && trials <= maxSearchTrials) {
            searchCenter
                .setX(topPoint.x + (trials % 2 === 0 ? trials * intersectionShiftStep : 0))
                .setZ(topPoint.z + (trials % 2 === 0 ? 0 : trials * intersectionShiftStep))
            raycaster.set(searchCenter, raycaster.ray.direction)
            intersections.push(...raycaster.intersectObjects(furnitureMeshList).map(({ point }) => point))
            trials += 1
        }

        // Get the nearest intersection point to the furniture's center for relatively tall items
        // Note chairs and tables are excluded as their shape easily achieves the correct placement with the first intersection
        // Note far intersections from center are excluded for storageunits items since they lead to bad pin placements
        const centerPoint = this.renderObject.localToWorld(centerPointLocal)
        if (!this.furnitureData.hasTallLegs && furnitureItemHeight > leastNonTableHeight) {
            intersections.sort((a, b) => a.distanceTo(centerPoint) - b.distanceTo(centerPoint))
        }
        if (this.furnitureData.isCubicStructure) {
            intersections = intersections
                .filter(intersection => intersection.distanceTo(centerPoint) < maxPointDistanceToCenter)
        }

        // Save the intersection point, if any, in local coordinates
        this.tagPointLocal = intersections[0]
            ? centerPointLocal.clone().setY(this.renderObject.worldToLocal(intersections[0]).y)
            : centerPointLocal.clone()
    }

    resizeTextures(rootObject) {
        const textureMap = new Map()
        const textureSize = this.Renderer.maxTextureSize
        const processTexture = (texture) => {
            if (!texture || textureMap.has(texture.uuid)) {
                return
            }
            textureMap.set(texture.uuid, {
                old: texture,
                resized: this.Renderer.textureHelper.getResizedCopy(texture, textureSize, textureSize)
            })
        }
        const reassignTexture = (material, propertyName) => {
            if (!material[propertyName]) {
                return
            }
            const info = textureMap.get(material[propertyName].uuid)
            if (info && info.resized) {
                // eslint-disable-next-line no-param-reassign
                material[propertyName] = info.resized
            }
        }
        rootObject.traverse((child) => {
            if(!child.material) {
                return
            }
            processTexture(child.material.map)
            processTexture(child.material.normalMap)
            processTexture(child.material.roughnessMap)
            processTexture(child.material.metalnessMap)
            processTexture(child.material.aoMap)
        })
        rootObject.traverse((child) => {
            if (!child.material) {
                return
            }
            reassignTexture(child.material, 'map')
            reassignTexture(child.material, 'normalMap')
            reassignTexture(child.material, 'roughnessMap')
            reassignTexture(child.material, 'metalnessMap')
            reassignTexture(child.material, 'aoMap')
        })
        textureMap.forEach((value) => {
            value.old.dispose()
            if (value.old.image && value.old.image.close) {
                value.old.image.close()
            }
        })
        textureMap.clear()
    }

    async loadFurniture() {
        this.clearObject()
        this.addPlaceholder()

        this.metrics.msStartLoad = Date.now()

        // this.Renderer.furnitureLoader.loadFurniture(
        this.Renderer.furnitureManager.getGlb(
            this.furnitureData.modelUrl,
            (progress) => {
                // Either the model size or the total or the largest of loaded, 4mb
                const defaultSize = (progress.total || Math.max(4000000, progress.loaded))
                this.setLoadSize(defaultSize)

                // Progress is the uncompressed size so get the total from the model data
                const { uncompressedModelSize} = this.furnitureData
                const total = (uncompressedModelSize || defaultSize)
                this.updatePlaceholder(progress.loaded / total)
            },
            () => this.update(),
            this.Renderer,
            this.metrics
        ).then((furnishing) => {
            this.metrics.msEndLoad = Date.now()
            this.metrics.sLoadTime = (this.metrics.msEndLoad - this.metrics.msStartLoad) * 0.001
            /* eslint-disable no-param-reassign */
            furnishing.name = this.name
            furnishing.visible = this.visible
            furnishing.isFurniture = true
            furnishing.matrix.identity()
            furnishing.matrix.decompose(furnishing.position, furnishing.quaternion, furnishing.scale)
            furnishing.updateMatrixWorld(true)
            this.clearObject()

            // Item loading may be cancelled by the moment loading finished, the item might be already in deleted state
            // and the resources are already cleared by the CompositionItem's componentWillUnmount
            if (this.isDeleted) {
                this.metrics.msEndFlush = Date.now()
                this.metrics.sFlushTime = (this.metrics.msEndFlush - this.metrics.msEndLoad) * 0.001
                this.metrics.sTotalTime = (this.metrics.msEndFlush - this.metrics.msStartLoad) * 0.001
                return
            }

            if (isMobile()) {
                this.resizeTextures(furnishing)
                if (this.Renderer.ThreeWGLrenderer) {
                    furnishing.traverse(obj => {
                        obj.layers.set(this.ThreeNtvLayer)
                        const keyCount = Object.keys(obj.material).length
                        for (let i = 0; i < keyCount; i += 1) {
                            const key = Object.keys(obj.material)[i];
                            if (key.endsWith('map') && (obj.material[key] && obj.material[key].image)) {
                                this.Renderer.ThreeWGLrenderer.initTexture(obj.material[key])
                                delete obj.material[key].image
                            }
                        }
                    })
                }
            }
            // ThreeJS saves the texture memory on RAM to allow future texture updates, so to reduce memory footprint we
            // force early upload the material maps to GPU, delete them from the RAM memory
            if (this.Renderer.ThreeWGLrenderer) {
                furnishing.traverse(obj => {
                    obj.layers.set(this.ThreeNtvLayer)
                    // Temporary workaround: some transparent glbs have base color map all black with 
                    // zero alpha, which zeroes the opacity value, same can happen if alphaMap is all black too.
                    // Until glbs have proper transmmission setup, we'll aplly temporary value here.
                    if (!(obj.material && obj.material.isMeshStandardMaterial)) return
                    if (obj.material.transparent) {
                        if (obj.material.alphaMap) {
                            obj.material.alphaMap.dispose()
                            obj.material.alphaMap = null
                        }
                        if (obj.material.map) {
                            obj.material.map.dispose()
                            obj.material.map = null
                        }
                        obj.material.opacity = 0.35
                    }
                })
            }
            this.bbox.expandByObject(furnishing)
            this.boundingBox = WallspiceWebgl.createBoundingBox(this.bbox)
            this.boundingBox.visible = false
            furnishing.add(this.boundingBox)
            this.setObject(furnishing)
            this.addJustLoaded()
            this.initializeTagPointPosition()
            this.update(() => this.onUpdate({ metrics: this.metrics }))

            this.metrics.msEndFlush = Date.now()
            this.metrics.sFlushTime = (this.metrics.msEndFlush - this.metrics.msEndLoad) * 0.001
            this.metrics.sTotalTime = (this.metrics.msEndFlush - this.metrics.msStartLoad) * 0.001

            if (this.furnitureData.isRug) {
                furnishing.isFurniture = false
                this.isRug = true
                const childMeshes = []
                furnishing.traverse((child) => {
                    if (!child.isMesh) {
                        return
                    }
                    childMeshes.push(child)
                })
                childMeshes.forEach((child) => {
                    const backsideMesh = child.clone()
                    backsideMesh.scale.y *= 0.5
                    backsideMesh.matrixAutoUpdate = true

                    backsideMesh.material = new THREE.MeshStandardMaterial({
                        map: child.material.map,
                        color: child.material.color,
                        normalMap: child.material.normalMap,
                        metalness: child.material.metalness,
                        roughness: child.material.roughness,
                        metalnessMap: child.material.metalnessMap,
                        roughnessMap: child.material.roughnessMap,
                        aoMap: child.material.aoMap,

                        stencilWrite: true,
                        stencilFunc: THREE.AlwaysStencilFunc,
                        stencilRef: 3,
                        stencilFail: THREE.ReplaceStencilOp,
                        stencilZFail: THREE.ReplaceStencilOp,
                        stencilZPass: THREE.ReplaceStencilOp,
                    })

                    child.add(backsideMesh)
                    child.material.stencilWrite = true
                    child.material.stencilFunc = THREE.NotEqualStencilFunc
                    child.material.stencilRef = 2
                })
            }
        }).catch((error) => {
            this.metrics.msEndLoad = Date.now()
            this.metrics.sLoadTime = (this.metrics.msEndLoad - this.metrics.msStartLoad) * 0.001
            this.metrics.failed = true
            if (error) {
                // eslint-disable-next-line no-console
                console.error(error)
                this.metrics.errorMessage = error.message
                Sentry.captureException(new Error(error))
            }
            this.onUpdate({ metrics: this.metrics })
        })
    }

    endIsNigh() {
        if(this.parent) {
            this.parent.removeChild(this)
        }
        super.endIsNigh()
    }

    onDragStart() {
        super.onDragStart()

        if (this.stackedPlane) {
            const surface = this.Renderer.getStackingSurface(this.stackedPlane)
            if (surface) {
                surface.setHighlighted(true)
            }
        }
    }

    onDragFinish() {
        super.onDragFinish()

        if (this.stackedPlane) {
            const surface = this.Renderer.getStackingSurface(this.stackedPlane)
            if (surface) {
                surface.setHighlighted(false)
            }
        }
    }
}

export default FurnitureItem
